import type { FormVersion } from "../state/formVersion";
import { useFormVersionDispatch, useFormVersionState } from "../state/formVersion";
import { type SetStoreFunction } from "solid-js/store";
import { cloneDeep, isEmpty, isEqual, isNil, isString, lowerFirst } from "lodash-es";
import type { FieldType } from "../types";
import { Scope } from "../types";
import { toField } from "../fields/fields";
import { useTranslation } from "../state/i18n";
import type { TFunction } from "i18next";
import { initialized } from "../utils/initializedUtil";
import FieldId from "../fields/FieldId";
import FormSettingsService from "./FormSettingsService";
import type SubformId from "../state/SubformId";
import type { Accessor } from "solid-js";
import { batch, createMemo, on } from "solid-js";
import { trackStore } from "@solid-primitives/deep";
import type { BaseField, FieldConfig, FieldDataNameProperties, FieldLabelTextProperties } from "../fields/types";
import IntegrationConfigurationService from "./IntegrationConfigurationService";
import DataNameService from "./DataNameService";
import type { StringTemplateTypes } from "../utils/stringTemplateUtil.ts";

export type FieldServiceType = {
  getFields: () => FieldConfig[];
  getField: <T = Record<string, unknown>>(fieldId: FieldId) => FieldConfig<T> | undefined;
  getFieldsByType: <T = Record<string, unknown>>(type: FieldType) => FieldConfig<T>[];
  getFieldsByTypesExcept: <T = Record<string, unknown>>(types: FieldType[]) => FieldConfig<T>[];
  getFieldsByTypes: <T = Record<string, unknown>>(types: FieldType[]) => FieldConfig<T>[];
  createField: (fieldType: FieldType, otherProps?: Record<string, unknown>) => FieldConfig;
  removeField: (fieldId: FieldId) => void;
  addFieldBefore: (field: FieldConfig, beforeFieldId: FieldId) => void;
  addFieldToEnd: (field: FieldConfig) => void;
  moveFieldBefore: (fieldId: FieldId, beforeFieldId: FieldId) => void;
  moveFieldToEnd: (fieldId: FieldId) => void;
  duplicateField: (fieldId: FieldId, duplicateToSubform?: SubformId) => FieldId;
  duplicateFieldProperties: (
    fieldId: FieldId,
    updateReference: (stringTemplatePart: StringTemplateTypes) => StringTemplateTypes,
  ) => void;
  setFieldProperty: (fieldId: FieldId, key: string, value: unknown) => void;
  setDataNameProperty: (fieldId: FieldId, value: string) => void;
  setLabelProperty: (fieldId: FieldId, value: string) => void;
};

let setFormVersionStore: SetStoreFunction<FormVersion> | null = null;
let formVersionStore: FormVersion | null = null;
let t: TFunction | null = null;
let fieldMap: Accessor<Map<string, FieldConfig<unknown>>> | null = null;

const init = () => {
  setFormVersionStore = useFormVersionDispatch();
  formVersionStore = useFormVersionState();
  t = useTranslation().t;

  // We don't want to keep multiple reactive instances of this map floating around
  // oxlint-disable-next-line solid/reactivity
  fieldMap = createMemo(
    on(
      () => trackStore(getFormVersionStore().fields),
      (fields) => {
        const fieldMap = new Map<string, FieldConfig<unknown>>();
        fields.forEach((field) => {
          fieldMap.set(field.uid.toString(), field);
        });
        return fieldMap;
      },
    ),
  );
};

const getSetFormVersionStore = () => initialized(setFormVersionStore, "setFormVersionStore");
const getFormVersionStore = () => initialized(formVersionStore, "formVersionStore");
const getT = () => initialized(t, "t");

const create = (fieldScopingFilter: (field: FieldConfig) => boolean, subformId?: SubformId): FieldServiceType => {
  const getScopedFields = () => getFormVersionStore().fields.filter(fieldScopingFilter);

  const getScopedField = <T = Record<string, unknown>>(fieldId: FieldId): FieldConfig<T> | undefined => {
    if (isNil(fieldMap)) {
      return undefined;
    }
    const field = fieldMap()?.get(fieldId.toString());
    if (isNil(field) || !fieldScopingFilter(field as FieldConfig)) {
      return undefined;
    }
    return field as FieldConfig<T>;
  };

  const setDataName = (fieldId: FieldId, value: string) => {
    getSetFormVersionStore()(
      "fields",
      (field) => fieldId.equals(field.uid) && toField(field.type).hasData,
      "properties",
      "data_name",
      DataNameService.getValidDataNameForField(value, fieldId, subformId),
    );
  };

  const setDataNameBasedOnLabel = (fieldId: FieldId, value: string) => {
    if (isEmpty(value) || !isString(value)) {
      return;
    }
    const field = getScopedField(fieldId);
    if (field?.state !== "unpublished") {
      return;
    }
    const properties = field.properties as FieldLabelTextProperties & FieldDataNameProperties;
    const labelMatchesDataName =
      isString(properties.label_text) &&
      isEqual(
        lowerFirst(DataNameService.getValidDataNameForField(properties.label_text, fieldId)),
        properties.data_name,
      );
    if (!labelMatchesDataName) {
      return;
    }
    setDataName(fieldId, lowerFirst(value));
  };

  const initializeDataField = (field: BaseField<unknown, unknown>, otherProps: Record<string, unknown>) => {
    const initializedField = field.init(subformId ?? Scope.ROOT, { ...otherProps });
    const properties = initializedField.properties as FieldLabelTextProperties & FieldDataNameProperties;
    return {
      ...initializedField,
      properties: {
        ...properties,
        // Base dataName on the label text when available. Pre-defined dataNames from favourites take precedence.
        data_name: DataNameService.getValidDataNameForField(
          properties.data_name ?? lowerFirst(properties.label_text ?? getT()(field.titleKey)),
          undefined,
          subformId,
        ),
      },
    };
  };

  return {
    getFields: getScopedFields,
    getField: getScopedField,
    getFieldsByType: <T = Record<string, unknown>>(type: FieldType): FieldConfig<T>[] =>
      getScopedFields().filter((field) => field.type === type) as FieldConfig<T>[],
    getFieldsByTypesExcept: <T = Record<string, unknown>>(types: FieldType[]): FieldConfig<T>[] =>
      getScopedFields().filter((field) => !types.includes(field.type)) as FieldConfig<T>[],
    getFieldsByTypes: <T = Record<string, unknown>>(types: FieldType[]): FieldConfig<T>[] =>
      getScopedFields().filter((field) => types.includes(field.type)) as FieldConfig<T>[],
    createField: (fieldType: FieldType, otherProps: Record<string, unknown> = {}): FieldConfig => {
      const baseField = toField(fieldType);
      return baseField.hasData
        ? initializeDataField(baseField, otherProps)
        : baseField.init(subformId ?? Scope.ROOT, { ...otherProps });
    },
    removeField: (fieldId: FieldId) => {
      getSetFormVersionStore()("fields", (prev) => prev.filter((field) => !fieldId.equals(field.uid)));
      FormSettingsService.removeFromFilteredFields(fieldId);
      IntegrationConfigurationService.removeEmailAttachments(fieldId, subformId ?? Scope.ROOT);
      IntegrationConfigurationService.removeGoogleSheetColumnOrder(fieldId);
    },
    addFieldBefore: (field: FieldConfig, beforeFieldId: FieldId): void => {
      getSetFormVersionStore()("fields", (prev: FieldConfig<{ data_name?: string }>[]) => {
        const beforeFieldIdIndex = prev.findIndex((field) => field.uid.equals(beforeFieldId));
        return [...prev.slice(0, beforeFieldIdIndex), field, ...prev.slice(beforeFieldIdIndex)];
      });
    },
    addFieldToEnd: (field: FieldConfig): void => {
      getSetFormVersionStore()("fields", (prev) => [...prev, field]);
    },
    moveFieldBefore: (fieldId: FieldId, beforeFieldId: FieldId) => {
      getSetFormVersionStore()("fields", (prev) => {
        if (fieldId.equals(beforeFieldId)) {
          return prev;
        }

        const from = prev.findIndex((field) => field.uid.equals(fieldId));
        const to = prev.findIndex((field) => field.uid.equals(beforeFieldId));

        const toMove = prev[from];
        const listWithout = [...prev.slice(0, from), ...prev.slice(from + 1)];
        const newIndex = to > from ? to - 1 : to;

        return [...listWithout.slice(0, newIndex), toMove, ...listWithout.slice(newIndex)];
      });
    },
    moveFieldToEnd: (fieldId: FieldId) => {
      getSetFormVersionStore()("fields", (prev) => {
        const from = prev.findIndex((field) => field.uid.equals(fieldId));
        const toMove = prev[from];

        const listWithout = [...prev.slice(0, from), ...prev.slice(from + 1)];

        return [...listWithout, toMove];
      });
    },
    duplicateField: (fieldId: FieldId, duplicateToSubform?: SubformId) => {
      const clonedFieldId = FieldId.random();
      getSetFormVersionStore()("fields", (prev: FieldConfig[]) => {
        const fieldIndex = prev.findIndex((field) => fieldId.equals(field.uid));
        if (fieldIndex === -1) {
          return prev;
        }

        const clonedField: FieldConfig = {
          ...cloneDeep(prev[fieldIndex]),
          uid: clonedFieldId,
          state: "unpublished",
        };
        if (!isNil(clonedField.properties.data_name)) {
          clonedField.properties.data_name = DataNameService.getValidDataNameForField(
            clonedField.properties.data_name as string,
            undefined,
            subformId,
          );
        }
        if (!isNil(duplicateToSubform)) {
          clonedField.scope = duplicateToSubform;
        }

        return [...prev.slice(0, fieldIndex + 1), clonedField, ...prev.slice(fieldIndex + 1)];
      });
      return clonedFieldId;
    },
    duplicateFieldProperties: (fieldId, updateReference) => {
      getSetFormVersionStore()("fields", (prev: FieldConfig[]) => {
        const fieldIndex = prev.findIndex((field) => fieldId.equals(field.uid));
        if (fieldIndex === -1) {
          return prev;
        }

        const clonedField: FieldConfig = {
          ...cloneDeep(prev[fieldIndex]),
        };

        const field = toField(clonedField.type);
        clonedField.properties = field.duplicateProperties(clonedField.properties, updateReference) as FieldConfig;

        return [...prev.slice(0, fieldIndex), clonedField, ...prev.slice(fieldIndex + 1)];
      });
    },
    setFieldProperty: (fieldId: FieldId, key: string, value: unknown) => {
      // Deep clone objects/arrays to prevent reference sharing
      const safeValue = typeof value === "object" && !isNil(value) ? cloneDeep(value) : value;

      getSetFormVersionStore()("fields", (field) => fieldId.equals(field.uid), "properties", key, safeValue);
    },

    setDataNameProperty: (fieldId: FieldId, value: string) => {
      if (isEmpty(value) || !isString(value)) {
        return;
      }
      setDataName(fieldId, value);
    },
    setLabelProperty: (fieldId: FieldId, value: string) => {
      batch(() => {
        setDataNameBasedOnLabel(fieldId, value);
        getSetFormVersionStore()("fields", (field) => fieldId.equals(field.uid), "properties", "label_text", value);
      });
    },
  };
};

const unscopedFieldService = create(() => true);

export default {
  init,
  create,
  getField: unscopedFieldService.getField,
  getAllFields: unscopedFieldService.getFields,
  getAllFieldsByType: unscopedFieldService.getFieldsByType,
  getFieldsByTypes: unscopedFieldService.getFieldsByTypes,
  getFieldsByTypesExcept: unscopedFieldService.getFieldsByTypesExcept,
};
